import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import { formatXOF } from "../../lib/i18n";
import { useStaffAuth } from "../../context/StaffAuthContext";
import { CalendarDays, Wallet, Users, Anchor, AlertTriangle, Clock, Star } from "lucide-react";
import { toast } from "sonner";

const STATUS_COLORS = {
  pending: "bg-[#FAFAF7] text-[#0A0A0A]/55 border-[#0A0A0A]/15",
  confirmed: "bg-[#B8922A]/10 text-[#B8922A] border-[#B8922A]/30",
  arrived: "bg-green-50 text-green-700 border-green-200",
  completed: "bg-blue-50 text-blue-700 border-blue-200",
  cancelled: "bg-red-50 text-red-700 border-red-200",
};
const STATUS_FR = {
  pending: "En attente", confirmed: "Confirmée", arrived: "Arrivée",
  completed: "Terminée", cancelled: "Annulée",
};
const OFFER_COLORS = {
  pass_day: "border-l-4 border-[#B8922A]",
  sunset: "border-l-4 border-orange-500",
  brunch: "border-l-4 border-green-600",
  le_kaai: "border-l-4 border-purple-500",
  hebergement: "border-l-4 border-blue-600",
};

function KpiCard({ icon: Icon, label, value, sub }) {
  return (
    <div className="bg-white border border-[#0A0A0A]/8 p-6">
      <div className="flex items-start justify-between mb-3">
        <div className="text-[0.65rem] uppercase tracking-[0.22em] text-[#0A0A0A]/55">{label}</div>
        <Icon size={16} className="text-[#B8922A]" />
      </div>
      <div className="font-display-serif text-3xl text-[#0A0A0A] leading-none">{value}</div>
      {sub && <div className="text-[0.7rem] text-[#0A0A0A]/45 mt-2">{sub}</div>}
    </div>
  );
}

export default function StaffDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user } = useStaffAuth();
  const isManager = user && (user.role === "manager" || user.role === "admin");

  useEffect(() => {
    let cancelled = false;
    api.get("/staff/dashboard")
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => {
        if (cancelled) return;
        const status = e.response?.status;
        if (status === 401 || status === 403) {
          // Interceptor already redirects to /staff/login on 401
          return;
        }
        toast.error(
          status
            ? `Erreur ${status} en chargeant le tableau de bord`
            : "Connexion au serveur impossible — vérifiez votre réseau",
        );
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading) return <div className="p-10 text-[#0A0A0A]/50">Chargement…</div>;
  if (!data) return (
    <div className="p-10 max-w-xl" data-testid="dashboard-error">
      <div className="text-red-600 mb-3">Impossible de charger le tableau de bord.</div>
      <div className="text-sm text-[#0A0A0A]/55 mb-4">
        Si l'erreur persiste, votre session a peut-être expiré. Reconnectez-vous, ou rechargez la page.
      </div>
      <div className="flex gap-2">
        <button onClick={() => window.location.reload()} className="px-4 py-2 text-[0.7rem] uppercase tracking-[0.22em] bg-[#B8922A] text-white hover:bg-[#a37e1f]">
          Recharger
        </button>
        <button onClick={() => { localStorage.removeItem("bbr_staff_session"); window.location.href = "/staff/login"; }} className="px-4 py-2 text-[0.7rem] uppercase tracking-[0.22em] border border-[#0A0A0A]/15 text-[#0A0A0A]/65 hover:border-[#0A0A0A]">
          Se reconnecter
        </button>
      </div>
    </div>
  );

  const { kpis, bookings_today, alerts, pole_breakdown } = data;
  const totalRevenue30d = (pole_breakdown || []).reduce((s, p) => s + (p.last_30d?.revenue || 0), 0) || 1;

  return (
    <div className="p-4 md:p-8 lg:p-10 max-w-7xl mx-auto" data-testid="staff-dashboard">
      <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="font-display-serif text-3xl md:text-4xl text-[#0A0A0A]">Tableau de bord</h1>
          <p className="text-sm text-[#0A0A0A]/55 mt-1">Vue opérationnelle de la journée</p>
        </div>
        {isManager && (
          <Link
            to="/staff/reservations/nouvelle"
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 text-[0.7rem] uppercase tracking-[0.22em] bg-[#B8922A] text-white hover:bg-[#a37e1f] transition-all self-start sm:self-auto"
            data-testid="dashboard-newbooking-cta"
          >
            + Nouvelle réservation
          </Link>
        )}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mb-5">
        <KpiCard icon={CalendarDays} label="Réservations du jour" value={kpis.bookings_today} />
        <KpiCard icon={Wallet} label="Revenus du jour" value={formatXOF(kpis.revenue_today)} />
        <KpiCard icon={Users} label="Clients attendus" value={kpis.guests_today} />
        <KpiCard icon={Anchor} label="Traversées prévues" value={kpis.crossings_today} />
      </div>

      {/* Feedback average — last 90 days */}
      {kpis.feedback_count > 0 && (
        <div className="bg-white border border-[#0A0A0A]/8 p-5 md:p-6 mb-10 grid grid-cols-1 sm:grid-cols-3 gap-5 items-center" data-testid="dashboard-feedback-avg">
          <div className="flex items-center gap-4">
            <Star className="text-[#B8922A]" size={32} strokeWidth={1.2} fill="#B8922A" />
            <div>
              <div className="text-[0.6rem] uppercase tracking-[0.22em] text-[#0A0A0A]/55">Satisfaction moyenne</div>
              <div className="font-display-serif text-3xl text-[#0A0A0A] mt-0.5" data-testid="feedback-average-value">
                {kpis.feedback_average?.toFixed(2) ?? "—"} <span className="text-base text-[#0A0A0A]/40">/ 5</span>
              </div>
            </div>
          </div>
          <div>
            <div className="text-[0.6rem] uppercase tracking-[0.22em] text-[#0A0A0A]/55">Score NPS</div>
            <div className="font-display-serif text-2xl text-[#0A0A0A] mt-0.5">
              {kpis.feedback_nps_avg ?? "—"}
              <span className="text-sm text-[#0A0A0A]/40 ml-2">/ 100</span>
            </div>
          </div>
          <div className="flex sm:justify-end">
            <Link
              to="/staff/feedback"
              className="inline-flex items-center gap-2 text-[0.65rem] uppercase tracking-[0.22em] text-[#0A0A0A]/70 hover:text-[#B8922A]"
              data-testid="dashboard-feedback-link"
            >
              Voir tous les retours · {kpis.feedback_count} sur 90 jours
            </Link>
          </div>
        </div>
      )}

      {/* Pôle breakdown */}
      {pole_breakdown && pole_breakdown.length > 0 && (
        <div className="bg-white border border-[#0A0A0A]/8 p-5 md:p-6 mb-10" data-testid="pole-breakdown-card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display-serif text-xl text-[#0A0A0A]">Activité par pôle</h2>
            <span className="text-[0.6rem] uppercase tracking-[0.22em] text-[#0A0A0A]/45">30 derniers jours</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            {pole_breakdown.map((p) => {
              const pct = Math.round(((p.last_30d?.revenue || 0) / totalRevenue30d) * 100);
              const POLE_BAR = {
                beach_club: "bg-[#B8922A]",
                hebergement: "bg-blue-500",
                corporate: "bg-slate-500",
                activites_events: "bg-rose-500",
                le_kaai: "bg-purple-500",
              };
              return (
                <Link
                  key={p.id}
                  to={`/staff/reservations?pole=${p.id}`}
                  className="block border border-[#0A0A0A]/8 p-3.5 hover:border-[#B8922A] transition-colors group"
                  data-testid={`pole-tile-${p.id}`}
                >
                  <div className="text-[0.58rem] uppercase tracking-[0.22em] text-[#0A0A0A]/55 truncate">{p.name_fr}</div>
                  <div className="text-2xl font-display-serif text-[#0A0A0A] mt-0.5">{p.last_30d?.count || 0}</div>
                  <div className="text-[0.7rem] text-[#B8922A] mt-0.5">{formatXOF(p.last_30d?.revenue || 0)}</div>
                  <div className="mt-2 h-1.5 bg-[#FAFAF7] overflow-hidden rounded-sm">
                    <div className={`h-full ${POLE_BAR[p.id] || "bg-[#B8922A]"}`} style={{ width: `${pct}%` }} />
                  </div>
                  <div className="text-[0.6rem] text-[#0A0A0A]/45 mt-1">{pct}% du CA · {p.today?.count || 0} aujourd'hui</div>
                </Link>
              );
            })}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Planning */}
        <div className="lg:col-span-2 bg-white border border-[#0A0A0A]/8 p-6">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-display-serif text-xl text-[#0A0A0A]">Planning du jour</h2>
            <span className="text-[0.65rem] uppercase tracking-[0.22em] text-[#0A0A0A]/45">
              {bookings_today.length} réservation{bookings_today.length > 1 ? "s" : ""}
            </span>
          </div>
          {bookings_today.length === 0 ? (
            <p className="text-sm text-[#0A0A0A]/45 py-8 text-center">Aucune réservation prévue aujourd'hui.</p>
          ) : (
            <ul className="space-y-2.5" data-testid="bookings-today-list">
              {bookings_today.map((b) => (
                <li
                  key={b.id}
                  className={`flex items-center gap-4 bg-[#FAFAF7] p-3.5 ${OFFER_COLORS[b.offer_type] || ""}`}
                  data-testid={`booking-row-${b.id.slice(0, 8)}`}
                >
                  <div className="text-[0.7rem] tabular-nums text-[#B8922A] font-medium w-12 text-center">
                    {b.boat_time || "—"}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-[#0A0A0A] truncate">
                      {b.participants?.[0] ? `${b.participants[0].surname} ${b.participants[0].name}` : b.phone}
                    </div>
                    <div className="text-[0.7rem] text-[#0A0A0A]/55">
                      {b.offer_name} · {b.adults}A {b.children > 0 ? `+ ${b.children}E` : ""}
                    </div>
                  </div>
                  <span className={`text-[0.62rem] uppercase tracking-[0.18em] px-2.5 py-1 border ${STATUS_COLORS[b.status] || STATUS_COLORS.pending}`}>
                    {STATUS_FR[b.status] || b.status}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Alerts */}
        <aside className="space-y-5">
          <div className="bg-white border border-[#0A0A0A]/8 p-5">
            <div className="flex items-center gap-2 mb-4">
              <Clock size={14} className="text-[#B8922A]" />
              <h3 className="text-[0.7rem] uppercase tracking-[0.22em] text-[#0A0A0A]/70">Arrivées imminentes</h3>
            </div>
            {alerts.imminent_arrivals.length === 0 ? (
              <p className="text-xs text-[#0A0A0A]/40">Aucune arrivée prévue dans les 2 prochaines heures.</p>
            ) : (
              <ul className="space-y-2">
                {alerts.imminent_arrivals.map((a) => (
                  <li key={a.booking_id} className="text-xs">
                    <span className="text-[#B8922A] font-medium">{a.boat_time}</span> ·{" "}
                    <span className="text-[#0A0A0A]">{a.offer}</span> · {a.guests} pers.
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="bg-white border border-[#0A0A0A]/8 p-5">
            <div className="flex items-center gap-2 mb-4">
              <AlertTriangle size={14} className="text-red-500" />
              <h3 className="text-[0.7rem] uppercase tracking-[0.22em] text-[#0A0A0A]/70">Impayés en attente</h3>
            </div>
            {alerts.unpaid_bookings.length === 0 ? (
              <p className="text-xs text-[#0A0A0A]/40">Aucun impayé en attente.</p>
            ) : (
              <ul className="space-y-2">
                {alerts.unpaid_bookings.slice(0, 6).map((u) => (
                  <li key={u.id} className="text-xs flex justify-between gap-3">
                    <span className="truncate text-[#0A0A0A]">{u.offer_name} · {u.date}</span>
                    <span className="text-[#B8922A] font-medium tabular-nums">{formatXOF(u.total_amount || 0)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}
